import React, { FC, useEffect, useState, useMemo } from 'react';
import { CreateIconComponentProps } from '../SvgIcons/CreateIconComponent';
import { IconCss, Segment, SegmentControl, PreviewWebsiteTooltip } from './SegmentControlV2.styles';

interface Segment {
  icon?: (p: CreateIconComponentProps) => JSX.Element;
  text?: string;
  key: string;
  hidden?: boolean;
  toolTipText?: string;
  // SegmentControl icons are used in other places, so this is for the new design
  segmentIcon?: React.ReactNode;
  segmentElementProperties?: {
    [k: string]: unknown;
  };
}

export type SegmentControlV2Props = {
  activeSegment?: number;
  onClick?: (key: string) => void;
  /** Object contains information for constructing segment;
   * segmentElementProperties can be used to pass html properties, i.e. role, href for accessibility
   */
  segments: Segment[];
  className?: string;
  compact?: boolean;
  iconOnly?: boolean;
  shouldShowToolTip?: boolean;
  light?: boolean;
  isInCollapse?: boolean;
};

const SegmentControlV2: FC<SegmentControlV2Props> = ({
  activeSegment = 0,
  segments = [],
  onClick = () => {
    /* do nothing */
  },
  className,
  compact,
  iconOnly,
  shouldShowToolTip,
  light,
  isInCollapse,
}) => {
  const [width, setWidth] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState<number>(activeSegment);
  const [ready, setReady] = useState(false);

  // Used to calculate width and offset of active segment control
  const segmentRefs = useMemo(
    () => segments.map(() => React.createRef<HTMLButtonElement>()),
    [segments]
  );

  useEffect(() => setActiveSegmentIndex(activeSegment), [activeSegment]);

  // Determine css properties for active segment
  const activeSegmentRef = segmentRefs[activeSegmentIndex];
  const { offsetWidth, offsetLeft } = activeSegmentRef.current || {
    offsetLeft: null,
    offsetWidth: null,
  };

  useEffect(() => {
    if (isInCollapse) {
      if (offsetWidth !== null && offsetLeft !== null) {
        setWidth(offsetWidth);
        setTranslateX(offsetLeft);
        setReady(true);
      }
    } else {
      const localActiveSegmentRef = segmentRefs[activeSegmentIndex];
      const { offsetWidth: offsetWidthLocal, offsetLeft: offsetLeftLocal } =
        localActiveSegmentRef.current || {
          offsetWidthLocal: undefined,
          offsetLeftLocal: undefined,
        };

      if (offsetWidthLocal !== undefined && offsetLeftLocal !== undefined) {
        setWidth(offsetWidthLocal);
        setTranslateX(offsetLeftLocal);
        setReady(true);
      }
    }
  }, [activeSegmentIndex, segmentRefs, offsetWidth, offsetLeft, isInCollapse]);

  const handleSegmentClick = (index: number) => {
    setActiveSegmentIndex(index);
    onClick(segments[index].key);
  };

  if (segments.length === 0) return null;

  return (
    <SegmentControl
      className={className}
      ready={ready}
      activeWidth={width}
      activeTranslateX={translateX}
      iconOnlyStyling={iconOnly}
      lightVariantStyling={light}
      compactHeight={compact}
    >
      {segments.map((segment, index) => {
        const IconComponent = segment.icon;
        return (
          <Segment
            isActive={index === activeSegmentIndex}
            shouldHide={segment.hidden}
            onClick={() => handleSegmentClick(index)}
            key={segment.key}
            ref={segmentRefs[index]}
            type="button"
            lightVariantStyling={light}
            {...segment.segmentElementProperties}
          >
            {IconComponent && <IconComponent css={IconCss} height={12} width={12} />}
            {shouldShowToolTip && <PreviewWebsiteTooltip text={segment.toolTipText} popIn />}
            {segment.segmentIcon}
            {segment.text}
          </Segment>
        );
      })}
    </SegmentControl>
  );
};

export default SegmentControlV2;
